<template>
  <ag-grid-vue v-if="row_data !== undefined" class="ag-theme-alpine" :columnDefs="column_defs"
    :defaultColDef="default_cols" :rowData="row_data" :rowStyle="row_style"
    :style="`height: ${grid_height}px; width: auto; margin-top: 20px; overflow-x: hidden;`" rowHeight="60"
    :alwaysShowHorizontalScroll="false" :pagination="true" :paginationPageSize="16" :gridOptions="gridOptions"
    @grid-ready="onGridReady" />
</template>

<script setup lang="js">
import { AgGridVue } from 'ag-grid-vue3';
import { onMounted, onUnmounted, ref, computed, watch, render, h } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import agg from '@/methods/ag_grid_toolbox';
import TooltipHeader from '@/components/tables/TooltipHeader.vue';
import StarButton from '@/components/tables/StarButton.vue';


const store = useStore();
const router = useRouter();
const column_defs = computed(() => store.state.ag_grid_coldefs[props?.table_id]);
const row_data = computed(() => store.state.ag_grid_rowdata[props?.table_id]);
const row_keys = computed(() => store.state.ag_grid_rowkeys[props?.table_id]);
const devmode = computed(() => store.state.devmode);
const selected_variants = computed(() => store.state.presentation_selected_variants);
const details_width = ref('150');
const select_width = ref('120');

const default_cols = ref()
/* Grid height is set to 14*60 + 100 for 14 rows*/
const grid_height = computed(() => {
  var temp_height = Math.min(940, (row_keys.value.length + 1) * 60 + 40);
  if (props?.maxHeight) temp_height = Math.min(props?.maxHeight, temp_height);
  return temp_height;
})
const fusions = computed(() => store.getters.fusions);

const gridOptions = {};

/**
 * @prop showSelectColumn - Dictates whether to show the select column (i.e. the blue star) on this table.
 * @prop maxHeight - Can be passed to limit this table's maximum height (in px).
 * @prop table_id - ID for the table. Important for HTML query selections, so it should be unique.
 */
const props = defineProps({
  maxHeight: Number,
  table_id: String,
  showSelectColumn: Boolean,
})

/**
 * When this table is loaded, the following steps are performed:
 * 1. Default column definitions, set in ag_grid_toolbox.js, are used.
 * 2. Specific column definitions for fusions are created.
 * 3. Fusion data is turned into an AG Grid RowData object.
 * 4. Columns are resized and the page is set to 0.
 * 5. Event listeners for sorting and resizing are added.
 */
onMounted(() => {
  default_cols.value = agg.createDefaultColDefs();
  fusionToRowData();
  createColDefs();
  gridOptions.api?.sizeColumnsToFit();
  gridOptions.api?.paginationGoToPage(0);
  window.addEventListener('sort-grid', (e) => onSort(e))
  window.addEventListener('resize', resizeGrid);
});

/**
 * Event listeners for sorting and resizing are removed on unmounting the component.
 */
onUnmounted(() => {
  window.removeEventListener('sort-grid', (e) => onSort(e))
  window.removeEventListener('resize', resizeGrid);
});

/**
 * Called when the sort-grid window event is caught. Applies a specific sort to the grid and refreshes it.
 * Will sort based on a targeted column based on a provided sorting method (asc, desc, none)
 * @param {CustomEvent} e - Event that contains sorting information (column, desired order)
 */
const onSort = (e) => {
  gridOptions.columnApi?.applyColumnState({
    state: [{ colId: e.detail.column.toLowerCase(), sort: e.detail.order }],
    defaultState: { sort: null },
  });
  gridOptions.api?.refreshCells();
}

/**
 * Once the grid is ready, it is refreshed and the select column is hidden if indicated by the props.
 * Then, it is resized.
 */
const onGridReady = () => {
  gridOptions.api?.refreshCells();
  if (!props.showSelectColumn) {
    gridOptions.columnApi?.setColumnVisible('select', false)
  }
  resizeGrid();
}

/**
 * Resizes/re-renders specific elements of the grid based on the screen width.
 * Sizes should be set in accordance with any media queries.
 */
const resizeGrid = () => {
  gridOptions.api?.sizeColumnsToFit();
  let current_coldefs = gridOptions.api?.getColumnDefs();

  // Sizes below 1300
  if (window.innerWidth < 1300) {
    current_coldefs.forEach((def, _) => {
      def.headerComponent = null;
      def.headerName = shortened_headers[def.colId];

      // Sizes below 1000
      if (window.innerWidth < 1000) {
        if (def.colId == 'details') {
          def.width = '60';
          def.maxWidth = '60';
          def.minWidth = '60';
          def.cellRenderer = (param) => addRouterLink(param, 'small')
        }
        if (def.colId == 'select') {
          def.width = '50';
          def.maxWidth = '50';
          def.minWidth = '50';
        }
      }
      // Sizes between 1000 and 1300
      else {
        if (def.colId == 'details') {
          def.width = '80';
          def.maxWidth = '80';
          def.minWidth = '80';
          def.cellRenderer = (param) => addRouterLink(param, 'small')
        }
        if (def.colId == 'select') {
          def.width = '60';
          def.maxWidth = '60';
          def.minWidth = '60';
        }
      }
    });
  }
  // Normal size (> 1300)
  if (window.innerWidth > 1300) {
    current_coldefs.forEach((def, index) => {
      def.headerComponent = TooltipHeader;
      def.headerName = long_headers[def.colId];
      if (def.colId == 'details') {
        def.width = '120';
        def.maxWidth = '120';
        def.minWidth = '120';
        def.cellRenderer = (param) => addRouterLink(param, 'full')
      }
      if (def.colId == 'select') {
        def.width = '100';
        def.maxWidth = '100';
        def.minWidth = '100';
      }
    })
  }
  gridOptions.api?.setColumnDefs(current_coldefs);
  gridOptions.api?.redrawRows();
}

/**
 * Creates table column definitions. These are specific for this table.
 * Saves the definitions in the column_defs ref (see below) and applies them to the grid.
 * If keys and key_values refs are defined below, they are used to automatically create some column definitions.
 */
const createColDefs = () => {
  let new_column_defs = [];

  // Selection Column
  if (props.showSelectColumn && devmode.value) {
    new_column_defs.push({
      headerName: 'Select', headerClass: 'presentation-header', colId: 'select',
      cellRenderer: (param) => {
        let container = document.createElement("div")
        let fusion_id = param?.data[key_headers.value[0]].fusion1 + '-' + param?.data[key_headers.value[0]].fusion2;
        const vsb = h(StarButton, {
          id: fusion_id, onToggle: (id) => toggleSelectedVariant(id),
          selected: Object.keys(selected_variants.value).includes(fusion_id)
        });
        render(vsb, container);
        return container;
      },
      sortable: 'false', pinned: 'left', width: '120', maxWidth: '120', flex: 0,
      headerComponent: TooltipHeader, headerComponentParams: {title: 'Select', table: props?.table_id},
      cellStyle: {justifyContent: "center"}
    })
  }

  // Auto-generated columns
  for (const header of key_headers.value) {
    new_column_defs.push({
      headerName: header, colId: header.toLowerCase(), headerClass: 'presentation-header-left',
      headerComponent: TooltipHeader,
      headerComponentParams: { title: header, colid: header.toLowerCase(), tooltip_id: key_tooltip_ids.value[key_headers.value.indexOf(header)] },
      cellRenderer: (params) => {
        let value = params.data[header];
        if (typeof value === 'object' && value !== null) {
          return value.fusion1 + '<br>' + value.fusion2;
        } else {
          return value;
        }
      },
      cellStyle: { lineHeight: '120%', textAlign: 'left'}
    });
  }

  //Details column
  new_column_defs.push({ headerName: 'Details', sortable: 'false', cellRenderer: (param) => addRouterLink(param, 'full'),
   headerClass: 'presentation-header', pinned: 'right', width: '150', maxWidth: '150', flex: 0,
  headerComponent: TooltipHeader, headerComponentParams: {title: 'Details', table: props?.table_id}, colId: 'details', cellStyle: {justifyContent: "center"} });

  store.dispatch('setAGGColdefs', { defs: new_column_defs, id: props?.table_id });
  gridOptions.api?.setColumnDefs(new_column_defs);
};

/**
 * Turns fusion data into rows for the AG Grid.
 * Uses two refs (see below): row_data and keys.
 */
const fusionToRowData = () => {

  // Check if data is present
  let list_to_use = fusions.value;
  if (!list_to_use || list_to_use == {}) return;

  let new_row_data = [];

  for (let fusion_key of Object.keys(list_to_use)) {

    let fusion_entry = list_to_use[fusion_key];
    let new_row = {};
    /* Row data is created automatically based on the properties defined below */
    for (const [index, descriptor] of key_headers.value.entries()) {
      let path = keys.value[index];
      if (Array.isArray(path)) {
        new_row[descriptor] = {
          fusion1: agg.resolveDeepProperty(path[0], fusion_entry),
          fusion2: agg.resolveDeepProperty(path[1], fusion_entry)
        };
      } else {
        new_row[descriptor] = agg.resolveDeepProperty(path, fusion_entry);
      }
    }
    /* Link to fusion details */
    new_row['details_link'] = { path: store.getters.getRouterBaseURL + '/show-fusion-details/' + fusion_key};

    /* Dirty fusion re-formatting, could replace later (no perf. impact though) */
    try {
        new_row[key_headers.value[0]].fusion1 = new_row[key_headers.value[0]].fusion1.replace('CHR', 'chr')
        new_row[key_headers.value[0]].fusion2 = new_row[key_headers.value[0]].fusion2.replace('CHR', 'chr')
    } catch(error) {
        console.log(error)
    }

    new_row_data.push(new_row);
  }

  store.dispatch('setAGGRowdata', { data: new_row_data, id: props?.table_id });
  store.dispatch('setAGGRowkeys', { keys: Object.keys(list_to_use), id: props?.table_id });

};

/**
 * Toggles whether a fusion is part of the 'selected variants/genes' subset or not.
 * @param {String} id - Variant identifier (fus1-fus2)
 */
const toggleSelectedVariant = async (id) => {
  store.dispatch('togglePresentationVariantSelected', id);
  gridOptions.api?.refreshCells();
}

/**
 * Adds a router-link button to the details column.
 * @param {object} param - AG Grid parameters 
 */
const addRouterLink = (param, size = 'full') => {
  const link = document.createElement("div");
  link.innerText = "Details";

  let id_key = key_headers.value[0];
  if (param.data[id_key].fusion1 !== undefined && param.data[id_key].fusion2 !== undefined) {
    link.classList.add("details-button");
  } else {
    link.classList.add("details-button-disabled");
  }
  link.classList.add("text-button");
  if (size == 'small') {
    link.innerText = 'D.'
    link.classList.add("details-button-small")
  }
  link.addEventListener("click", e => {
    e.preventDefault();
    if (param.data[id_key].fusion1 !== undefined && param.data[id_key].fusion2 !== undefined) {
      router.push(param.data.details_link.path);
    }
  });
  return link;
}


/**
 * Redraws the grid once the loading process stops.
 * Somehow refreshCells() is not enough here, so all rows have to be redrawn.
 */
watch(() => store.getters.getProcessState('load-results'), () => {
  gridOptions.api?.redrawRows();
  gridOptions.api?.sizeColumnsToFit();
});

/**
 * Contains keys to be auto-extracted from the fusion object.
 * As fusions are "split" into two distinct parts, most of them are kept in lists.
 * This way they can be stitched back together into one table cell.
 */
const keys = ref([
  ['UTA_Adapter_fusion.input_1.qid', 'UTA_Adapter_fusion.input_2.qid'],
  ['UTA_Adapter_fusion.input_1.gene_name', 'UTA_Adapter_fusion.input_2.gene_name'],
  ['UTA_Adapter_fusion.input_1.transcript', 'UTA_Adapter_fusion.input_2.transcript'],
  ['UTA_Adapter_fusion.input_1.strand', 'UTA_Adapter_fusion.input_2.strand'],
  'UTA_Adapter_fusion.is_inframe'
]);

/* Contains headers for columns that should be auto-created from these properties, in the same order! */
/* keys can also contain values at the end that are not automatically used for columns. */
/* This is useful when columns require cellRenderers etc and have to be set up manually. */
const key_headers = ref(['Location', 'Gene', 'Transcript', 'Strand', 'Frameshift']);
/* Tooltip IDs for the according headers */
const key_tooltip_ids = ref(['fusion', 'gene_fusion', 'transcript_fusion', 'strand_fusion', "frameshift"])
const shortened_headers = {
  'location': 'Loc.',
  'gene': 'Gn.',
  'transcript': 'Tr.',
  'strand': 'St.',
  'frameshift': 'FS',
  'select': 'Sl.',
  'details': 'Dt.'
}
const long_headers = {
  'location': 'Location',
  'gene': 'Gene',
  'transcript': 'Transcript',
  'strand': 'Strand',
  'frameshift': 'Frameshift',
  'select': 'Select',
  'details': 'Details'
}

</script>